import React, { useState, useEffect, useCallback } from "react";
import { firestore } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import "../css/Profile.css";
import { showErrorToast } from '../utils/toastUtils';
import { rateLimiter } from '../utils/simpleRateLimiter';
import Navigation from './Navigation';
import { useParams } from 'react-router-dom';

// --- Simple in-memory cache for all friend profile data ---
const profileCache = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in ms

const FriendsProfile = () => {
    const { userId } = useParams();
    const [profileInfo, setProfileInfo] = useState({
        name: "",
        username: "",
        bio: "",
        profilePicture: "https://ui-avatars.com/api/?name=User&background=random",
    });
    const [booksRead, setBooksRead] = useState([]);
    const [wantToRead, setWantToRead] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalBooks: 0,
        readingStreak: 0,
        lastReadDate: null
    });
    const [activeTab, setActiveTab] = useState("books");

    // Helper function to get week number
    const getWeekNumber = useCallback((date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    }, []);

    // Load profile data when user changes
    useEffect(() => {
        const loadProfileData = async () => {
            if (!userId) {
                setLoading(false);
                return;
            }

            // Check rate limit before any data loading
            if (!rateLimiter.canMakeRequest(userId)) {
                setLoading(false);
                showErrorToast("Too many profile refreshes. Please wait a moment before trying again.");
                return;
            }

            const now = Date.now();
            const cached = profileCache[userId];
            if (cached && (now - cached.timestamp < CACHE_DURATION)) {
                setProfileInfo(cached.profileData);
                setBooksRead(cached.books);
                setWantToRead(cached.wantToRead);
                setStats(cached.stats);
                setLoading(false);
                return;
            }

            try {
                // Load user profile first
                const userRef = doc(firestore, "users", userId);
                const userDoc = await getDoc(userRef);
                const profileData = userDoc.exists() ? userDoc.data() : null;

                if (!profileData) {
                    showErrorToast("Profile not found");
                    setLoading(false);
                    return;
                }

                // Load books and want to read in parallel
                const [booksSnapshot, wantToReadSnapshot] = await Promise.all([
                    getDocs(query(
                        collection(firestore, "FinishedBook"),
                        where("User_ID", "==", userId)
                    )),
                    getDocs(query(
                        collection(firestore, "wantToRead"),
                        where("userId", "==", userId)
                    ))
                ]);

                // Process books data
                const books = booksSnapshot.docs.map(docSnap => {
                    const data = docSnap.data();
                    return {
                        title: data.Book_Title,
                        id: data.Book_ID,
                        finishedDate: data.Finished_date?.toDate(),
                        rating: data.Rating,
                        coverUrl: data.Cover_URL || "https://ui-avatars.com/api/?name=Book&background=random"
                    };
                }).sort((a, b) => b.finishedDate - a.finishedDate);

                // Process want to read data
                const wantToReadBooks = wantToReadSnapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: data.bookId,
                        title: data.title,
                        author: data.author,
                        coverUrl: data.coverUrl,
                        addedDate: data.addedDate?.toDate()
                    };
                }).sort((a, b) => b.addedDate - a.addedDate);

                // Calculate stats
                const totalBooks = books.length;
                let readingStreak = 0;
                let lastReadDate = null;

                if (books.length > 0) {
                    const sortedBooks = [...books].sort((a, b) => b.finishedDate - a.finishedDate);
                    lastReadDate = sortedBooks[0].finishedDate;

                    const weekSet = new Set();
                    sortedBooks.forEach(book => {
                        const date = book.finishedDate;
                        if (date) {
                            const year = date.getFullYear();
                            const week = getWeekNumber(date);
                            weekSet.add(`${year}-W${week}`);
                        }
                    });

                    let streak = 0;
                    let currentDate = new Date(lastReadDate);
                    while (true) {
                        const year = currentDate.getFullYear();
                        const week = getWeekNumber(currentDate);
                        if (weekSet.has(`${year}-W${week}`)) {
                            streak++;
                            currentDate.setDate(currentDate.getDate() - 7);
                        } else {
                            break;
                        }
                    }
                    readingStreak = streak;
                }

                const stats = {
                    totalBooks,
                    readingStreak,
                    lastReadDate
                };

                // Update cache
                profileCache[userId] = {
                    profileData: {
                        name: profileData.name || "",
                        username: profileData.username || "",
                        bio: profileData.bio || "",
                        profilePicture: profileData.profilePicture || "https://ui-avatars.com/api/?name=User&background=random"
                    },
                    books,
                    wantToRead: wantToReadBooks,
                    stats,
                    timestamp: now
                };

                setProfileInfo({
                    name: profileData.name || "",
                    username: profileData.username || "",
                    bio: profileData.bio || "",
                    profilePicture: profileData.profilePicture || "https://ui-avatars.com/api/?name=User&background=random"
                });
                setBooksRead(books);
                setWantToRead(wantToReadBooks);
                setStats(stats);
                setLoading(false);
            } catch (error) {
                console.error("Error loading profile data:", error);
                showErrorToast("Failed to load profile data");
                setLoading(false);
            }
        };

        loadProfileData();
    }, [userId, getWeekNumber]);

    const handleImageError = (e) => {
        e.target.onerror = null;
        e.target.src = "https://ui-avatars.com/api/?name=User&background=random";
    };

    if (loading) {
        return (
            <div className="profile-page">
                <div className="loading-spinner">Loading...</div>
            </div>
        );
    }

    return (
        <div className="profile-page">
            <Navigation />
            <div className="profile-card">
                <img
                    src={profileInfo.profilePicture || "https://ui-avatars.com/api/?name=User&background=random"}
                    alt={profileInfo.username || profileInfo.name || "User"}
                    className="profile-pic"
                    onError={handleImageError}
                />
                <h2>{profileInfo.username || profileInfo.name || "User"}</h2>
                <p>{profileInfo.bio || "No bio available"}</p>
            </div>

            <div className="profile-tabs">
                <button 
                    className={`tab-button ${activeTab === "books" ? "active" : ""}`}
                    onClick={() => setActiveTab("books")}
                >
                    Books
                </button>
                <button 
                    className={`tab-button ${activeTab === "wantToRead" ? "active" : ""}`}
                    onClick={() => setActiveTab("wantToRead")}
                >
                    Want to Read
                </button>
                <button 
                    className={`tab-button ${activeTab === "stats" ? "active" : ""}`}
                    onClick={() => setActiveTab("stats")}
                >
                    Stats
                </button>
            </div>

            {activeTab === "stats" && (
                <div className="stats-section">
                    <h3>Reading Stats</h3>
                    <div className="stats-detail">
                        <div className="stat-detail-item">
                            <h4>Books Read</h4>
                            <div className="stat-number">{stats.totalBooks}</div>
                            <p>Total books completed</p>
                        </div>
                        <div className="stat-detail-item">
                            <h4>Reading Streak</h4>
                            <div className="stat-number">{stats.readingStreak}</div>
                            <p>Consecutive weeks reading</p>
                        </div>
                        <div className="stat-detail-item">
                            <h4>Last Read</h4>
                            <div className="stat-text">
                                {stats.lastReadDate ? 
                                    new Date(stats.lastReadDate).toLocaleDateString() : 
                                    "Never"}
                            </div>
                            <p>Date of most recent book</p>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "books" && (
                <div className="books-read-section">
                    <h3>Books Read</h3>
                    <div className="books-grid" id="booksReadGrid">
                        {booksRead.map((book) => (
                            <div key={book.id} className="book-card">
                                <img 
                                    src={book.coverUrl || "https://ui-avatars.com/api/?name=Book&background=random"} 
                                    alt={book.title}
                                    className="book-cover"
                                    onError={handleImageError}
                                />
                                <div className="book-info">
                                    <h4>{book.title}</h4>
                                    <p>Finished: {book.finishedDate?.toLocaleDateString()}</p>
                                    {book.rating && (
                                        <div className="rating-section">
                                            <p>Rating:</p>
                                            <div className="rating-stars">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <span
                                                        key={star}
                                                        className={`star ${star <= book.rating ? 'filled' : ''}`}
                                                    >
                                                        ★
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <button 
                        className="scroll-indicator scroll-left" 
                        onClick={() => {
                            const grid = document.getElementById('booksReadGrid');
                            grid.scrollBy({ left: -300, behavior: 'smooth' });
                        }}
                    >
                        ←
                    </button>
                    <button 
                        className="scroll-indicator scroll-right" 
                        onClick={() => {
                            const grid = document.getElementById('booksReadGrid');
                            grid.scrollBy({ left: 300, behavior: 'smooth' });
                        }}
                    >
                        →
                    </button>
                </div>
            )}

            {activeTab === "wantToRead" && (
                <div className="want-to-read-section">
                    <h3>Want to Read ({wantToRead.length})</h3>
                    {wantToRead.length === 0 ? (
                        <p>No books in this want to read list yet.</p>
                    ) : (
                        <>
                            <div className="books-grid" id="wantToReadGrid">
                                {wantToRead.map((book) => (
                                    <div key={book.id} className="book-card">
                                        <img 
                                            src={book.coverUrl} 
                                            alt={book.title}
                                            onError={handleImageError}
                                        />
                                        <div className="book-info">
                                            <h4>{book.title}</h4>
                                            <p>{book.author}</p>
                                            <p className="added-date">
                                                Added: {book.addedDate ? new Date(book.addedDate).toLocaleDateString() : ""}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button 
                                className="scroll-indicator scroll-left" 
                                onClick={() => {
                                    const grid = document.getElementById('wantToReadGrid');
                                    grid.scrollBy({ left: -300, behavior: 'smooth' });
                                }}
                            >
                                ←
                            </button>
                            <button 
                                className="scroll-indicator scroll-right" 
                                onClick={() => {
                                    const grid = document.getElementById('wantToReadGrid');
                                    grid.scrollBy({ left: 300, behavior: 'smooth' });
                                }}
                            >
                                →
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default FriendsProfile; 